import React, { useState, useEffect, useContext, useCallback } from "react";
import { Layout } from "@/components/Layout";
import { API, AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Settings,
  Plus,
  Trash2,
  Edit,
  Zap,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const TARGET_SYSTEMS = [
  { value: "vinylflow", label: "VinylFlow" },
  { value: "localization", label: "Localization" },
  { value: "distribution", label: "Distribution" },
  { value: "other", label: "Other" },
];

export default function TargetsPage() {
  const { user } = useContext(AuthContext);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingTarget, setEditingTarget] = useState(null);
  const [testResults, setTestResults] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    system: "vinylflow",
    webhook_url: "",
    secret: "",
    is_active: true,
    is_mock: false,
  });

  const isAdmin = user?.role === "admin";

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/targets`, {
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to fetch targets");

      const data = await response.json();
      setTargets(data.targets);
    } catch (error) {
      toast.error("Failed to load targets");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const handleCreate = async () => {
    try {
      const response = await fetch(`${API}/targets`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Failed to create target");

      toast.success("Target created");
      setDialogOpen(false);
      resetForm();
      fetchTargets();
    } catch (error) {
      toast.error("Failed to create target");
    }
  };

  const handleUpdate = async () => {
    if (!editingTarget) return;

    try {
      const response = await fetch(`${API}/targets/${editingTarget.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          webhook_url: formData.webhook_url,
          secret: formData.secret || undefined,
          is_active: formData.is_active,
        }),
      });

      if (!response.ok) throw new Error("Failed to update target");

      toast.success("Target updated");
      setDialogOpen(false);
      setEditingTarget(null);
      resetForm();
      fetchTargets();
    } catch (error) {
      toast.error("Failed to update target");
    }
  };

  const handleDelete = async (targetId) => {
    try {
      const response = await fetch(`${API}/targets/${targetId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to delete target");

      toast.success("Target deleted");
      setDeleteConfirm(null);
      fetchTargets();
    } catch (error) {
      toast.error("Failed to delete target");
    }
  };

  const handleTest = async (targetId) => {
    setTestResults((r) => ({ ...r, [targetId]: { loading: true } }));

    try {
      const response = await fetch(`${API}/targets/${targetId}/test`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json();

      setTestResults((r) => ({
        ...r,
        [targetId]: {
          loading: false,
          status: data.status,
          statusCode: data.status_code,
          error: data.error,
        },
      }));

      if (data.status === "success") {
        toast.success("Connection successful");
      } else {
        toast.error(`Connection failed: ${data.error || `HTTP ${data.status_code}`}`);
      }
    } catch (error) {
      setTestResults((r) => ({
        ...r,
        [targetId]: { loading: false, status: "failed", error: error.message },
      }));
      toast.error("Test failed");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      system: "vinylflow",
      webhook_url: "",
      secret: "",
      is_active: true,
      is_mock: false,
    });
  };

  const openEditDialog = (target) => {
    setEditingTarget(target);
    setFormData({
      name: target.name,
      system: target.system,
      webhook_url: target.webhook_url,
      secret: "",
      is_active: target.is_active,
      is_mock: target.is_mock,
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingTarget(null);
    resetForm();
    setDialogOpen(true);
  };

  return (
    <Layout
      title="Target Systems"
      actions={
        isAdmin && (
          <Button onClick={openCreateDialog} data-testid="create-target-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Target
          </Button>
        )
      }
    >
      {!isAdmin && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="py-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <p className="text-sm text-amber-800">
              Admin access is required to manage targets. Contact your administrator.
            </p>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="card-surface">
              <CardContent className="p-6">
                <div className="skeleton-row h-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : targets.length === 0 ? (
        <Card className="card-surface">
          <CardContent className="py-16 text-center">
            <Settings className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No targets configured</p>
            {isAdmin && (
              <Button onClick={openCreateDialog} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Target
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {targets.map((target) => {
            const testResult = testResults[target.id];
            return (
              <Card
                key={target.id}
                className={`card-surface ${!target.is_active ? "opacity-60" : ""}`}
                data-testid={`target-card-${target.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {target.name}
                        {target.is_mock && (
                          <Badge variant="secondary" className="text-xs">
                            Mock
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {TARGET_SYSTEMS.find((s) => s.value === target.system)?.label ||
                          target.system}
                      </CardDescription>
                    </div>
                    <Badge
                      variant="outline"
                      className={target.is_active ? "status-badge-success" : "status-badge-neutral"}
                    >
                      {target.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="overline mb-1">Webhook URL</p>
                    <p className="text-sm font-mono truncate">{target.webhook_url}</p>
                  </div>

                  {testResult && (
                    <div
                      className={`flex items-center gap-2 p-2 rounded text-sm ${
                        testResult.status === "success"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-red-50 text-red-700"
                      }`}
                    >
                      {testResult.loading ? (
                        <span className="animate-pulse">Testing...</span>
                      ) : testResult.status === "success" ? (
                        <>
                          <CheckCircle className="w-4 h-4" />
                          Connected
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" />
                          {testResult.error || `HTTP ${testResult.statusCode}`}
                        </>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTest(target.id)}
                      disabled={testResult?.loading}
                      data-testid={`test-target-${target.id}`}
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      Test
                    </Button>

                    {isAdmin && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditDialog(target)}
                          data-testid={`edit-target-${target.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => setDeleteConfirm(target.id)}
                          data-testid={`delete-target-${target.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTarget ? "Edit Target" : "Add Target"}</DialogTitle>
            <DialogDescription>
              {editingTarget
                ? "Update the target configuration."
                : "Configure a new webhook target for event dispatch."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                placeholder="VinylFlow Production"
                data-testid="target-name-input"
              />
            </div>

            {!editingTarget && (
              <div className="space-y-2">
                <Label>Target System</Label>
                <Select
                  value={formData.system}
                  onValueChange={(value) => setFormData((f) => ({ ...f, system: value }))}
                >
                  <SelectTrigger data-testid="target-system-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TARGET_SYSTEMS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Webhook URL</Label>
              <Input
                value={formData.webhook_url}
                onChange={(e) => setFormData((f) => ({ ...f, webhook_url: e.target.value }))}
                placeholder="https://api.example.com/webhooks/inbound-events"
                data-testid="target-webhook-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{editingTarget ? "New Secret (leave blank to keep current)" : "Secret"}</Label>
              <Input
                type="password"
                value={formData.secret}
                onChange={(e) => setFormData((f) => ({ ...f, secret: e.target.value }))}
                placeholder="••••••••"
                data-testid="target-secret-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Active</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData((f) => ({ ...f, is_active: checked }))}
                data-testid="target-active-switch"
              />
            </div>

            {!editingTarget && (
              <div className="flex items-center justify-between">
                <Label>Mock Target (for testing)</Label>
                <Switch
                  checked={formData.is_mock}
                  onCheckedChange={(checked) => setFormData((f) => ({ ...f, is_mock: checked }))}
                  data-testid="target-mock-switch"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={editingTarget ? handleUpdate : handleCreate}
              disabled={!formData.name || !formData.webhook_url || (!editingTarget && !formData.secret)}
              data-testid="save-target-btn"
            >
              {editingTarget ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Target?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Events will no longer be dispatched to this target.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => handleDelete(deleteConfirm)}
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
